import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import DescriptionIcon from '@mui/icons-material/Description';
import HistoryIcon from '@mui/icons-material/History';

import { SectionContent, GoBackButton, StyledList } from '../../components';
import ChartHistoryDrawer from '../../components/common/ChartHistoryDrawer';
import SEO from '../../components/SEO';
import { seoData } from '../../data/seoData';
import { openPDF } from '../../utils';
import useStrategyAssets from '../../hooks/useStrategyAssets';
import useStrategyPerformance from '../../hooks/useStrategyPerformance';
import ROUTES from '../../config/routes';
import THEME from '../../constants/theme';
import { SECTION_SPACING, ELEMENT_SPACING } from '../../constants';
import merinLogo from '../../assets/icons/merin-logo.svg';

const MerinPage = () => {
  const { latestCharts, chartHistory, documents } = useStrategyAssets('merin');
  const { performance: strategyPerformance } = useStrategyPerformance('merin');
  const [chartView, setChartView] = useState('week');
  const [chartType, setChartType] = useState('performance');
  const [isLoading, setIsLoading] = useState(false);
  const [displayedSrc, setDisplayedSrc] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyChart, setHistoryChart] = useState(null);

  // Set initial chart once CMS assets load (ref prevents stale-closure lint warning)
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (!hasInitialized.current && latestCharts?.week?.performance) {
      hasInitialized.current = true;
      setDisplayedSrc(latestCharts.week.performance);
    }
  }, [latestCharts]);

  const getChartSrc = (view, type) => {
    const period = view === 'week' ? 'week' : 'month';
    return latestCharts?.[period]?.[type] ?? null;
  };

  const updateChart = (nextView, nextType) => {
    const viewToUse = nextView || chartView;
    const typeToUse = nextType || chartType;
    const nextSrc = getChartSrc(viewToUse, typeToUse);

    if (!nextSrc || nextSrc === displayedSrc) return;

    setIsLoading(true);
    const img = new Image();
    img.onload = () => {
      setDisplayedSrc(nextSrc);
      setIsLoading(false);
    };
    img.onerror = () => setIsLoading(false);
    img.src = nextSrc;
  };

  const handleChartViewChange = (e, v) => {
    if (!v || v === chartView) return;
    setChartView(v);
    updateChart(v, chartType);
  };

  const handleChartTypeChange = (e, v) => {
    if (!v || v === chartType) return;
    setChartType(v);
    updateChart(chartView, v);
  };

  const handleDocumentClick = docType => {
    switch (docType) {
      case 'full-factsheet':
        if (documents.factsheet) openPDF(documents.factsheet);
        break;
      case 'strategy-factsheet':
        if (documents.description) openPDF(documents.description);
        break;
      default:
        break;
    }
  };

  const fallbackPerformanceStats = [
    { label: 'Average ROR', value: '0.63%' },
    { label: 'Sharpe Ratio (1Y- Bill)', value: '0.52' },
    { label: 'Total Return Cumulative', value: '1.82%' },
    { label: 'Sortino Ratio (1Y T-Bill)', value: '1.06' },
    { label: 'Total Return Annualized', value: '7.50%' },
    { label: 'Excess Return', value: '0.25%' }
  ];

  const performanceStats =
    strategyPerformance?.performanceStats?.length > 0
      ? strategyPerformance.performanceStats
      : fallbackPerformanceStats;

  const heroTagline =
    strategyPerformance?.hero?.tagline ||
    'Dynamic Macro Overlay for Multi-Asset Portfolios';

  return (
    <>
      <SEO
        title={seoData.merin.title}
        content={seoData.merin.content}
        keywords={seoData.merin.keywords}
        canonical={seoData.merin.canonical}
        ogImage={seoData.merin.ogImage}
        ogType={seoData.merin.ogType}
        structuredData={seoData.merin.structuredData}
      />
      <Box sx={{ width: '100%', backgroundColor: THEME.COLORS.NEUTRAL_WHITE }}>
        <GoBackButton backTo={ROUTES.STRATEGIES.ROOT} backText='Strategies' />

        {/* Hero Section with Logo */}
        <Box id='hero' sx={{ mb: SECTION_SPACING }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: THEME.SPACING.SMALL
            }}
          >
            <img
              src={merinLogo}
              alt='Merin Logo'
              style={{
                height: '110px',
                objectFit: 'contain'
              }}
            />
          </Box>
          <Typography
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
              fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem' },
              color: THEME.COLORS.NEUTRAL_GRAY_DARK,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM
            }}
          >
            {heroTagline}
          </Typography>
        </Box>

        {/* Performance Overview Section */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.PRIMARY_BLUE,
              mb: ELEMENT_SPACING
            }}
          >
            Performance Overview
          </Typography>

          <SectionContent
            initialSpacing={false}
            text={[
              {
                text: "The chart below illustrates MERIN's systematic approach to capturing macro opportunities across G7 FX markets with adaptive risk management and consistent execution.",
                fontSize: '1.3rem',
                marginBottom: ELEMENT_SPACING,
                color: THEME.COLORS.NEUTRAL_GRAY_DARK
              }
            ]}
          />

          {/* Performance Chart with Weekly/Monthly toggle */}
          <Box
            sx={{
              mb: ELEMENT_SPACING,
              backgroundColor: '#f8f9fb',
              borderRadius: '8px',
              padding: { xs: 2, sm: 3, md: 4 },
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}
          >
            {/* ── Chart toggles – hidden while viewing a history chart ── */}
            {!historyChart && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  mb: 1.5
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.2,
                    justifyContent: 'flex-start',
                    flex: 1,
                    minWidth: 240
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.9rem',
                      color: THEME.COLORS.NEUTRAL_GRAY_DARK,
                      fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM
                    }}
                  >
                    Chart Type
                  </Typography>
                  <ToggleButtonGroup
                    value={chartType}
                    exclusive
                    onChange={handleChartTypeChange}
                    size='small'
                    aria-label='Merin chart type'
                    sx={{
                      '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                        px: 1.6,
                        py: 0.55,
                        borderColor: 'transparent',
                        backgroundColor: '#e8f0ff',
                        color: THEME.COLORS.PRIMARY_BLUE,
                        borderRadius: '4px',
                        marginLeft: 0.35,
                        '&:hover': {
                          backgroundColor: THEME.COLORS.PRIMARY_BLUE + '24',
                          borderColor: THEME.COLORS.PRIMARY_BLUE
                        }
                      },
                      '& .MuiToggleButton-root.Mui-selected': {
                        background: `linear-gradient(135deg, ${THEME.COLORS.PRIMARY_BLUE}, #1b84ff)`,
                        color: THEME.COLORS.NEUTRAL_WHITE,
                        borderColor: THEME.COLORS.PRIMARY_BLUE,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        '&:hover': {
                          background: THEME.COLORS.PRIMARY_BLUE,
                          color: THEME.COLORS.NEUTRAL_WHITE
                        }
                      }
                    }}
                  >
                    <ToggleButton
                      value='performance'
                      aria-label='Performance chart'
                    >
                      Performance
                    </ToggleButton>
                    <ToggleButton value='exposure' aria-label='Exposure chart'>
                      Exposure
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <ToggleButtonGroup
                  value={chartView}
                  exclusive
                  onChange={handleChartViewChange}
                  size='small'
                  aria-label='Merin chart timeframe'
                  sx={{
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                      px: 1.6,
                      py: 0.55,
                      borderColor: 'transparent',
                      backgroundColor: '#e8f0ff',
                      color: THEME.COLORS.PRIMARY_BLUE,
                      borderRadius: '4px',
                      marginLeft: 0.35,
                      '&:hover': {
                        backgroundColor: THEME.COLORS.PRIMARY_BLUE + '24',
                        borderColor: THEME.COLORS.PRIMARY_BLUE
                      }
                    },
                    '& .MuiToggleButton-root.Mui-selected': {
                      background: `linear-gradient(135deg, ${THEME.COLORS.PRIMARY_BLUE}, #1b84ff)`,
                      color: THEME.COLORS.NEUTRAL_WHITE,
                      borderColor: THEME.COLORS.PRIMARY_BLUE,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      '&:hover': {
                        background: THEME.COLORS.PRIMARY_BLUE,
                        color: THEME.COLORS.NEUTRAL_WHITE
                      }
                    }
                  }}
                >
                  <ToggleButton value='week' aria-label='Weekly chart'>
                    Weekly
                  </ToggleButton>
                  <ToggleButton value='month' aria-label='Monthly chart'>
                    Monthly
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            {/* ── History toolbar – below chart type buttons ──────────────────── */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: historyChart ? 'space-between' : 'flex-start',
                mb: historyChart ? 1.5 : 1,
                mt: !historyChart ? 1.5 : 0
              }}
            >
              <Button
                size='small'
                variant={historyOpen ? 'contained' : 'outlined'}
                startIcon={<HistoryIcon />}
                onClick={() => setHistoryOpen(h => !h)}
                aria-expanded={historyOpen}
                aria-controls='merin-history'
                sx={{
                  textTransform: 'none',
                  px: 1.4,
                  py: 0.5,
                  borderColor: THEME.COLORS.PRIMARY_BLUE,
                  color: historyOpen
                    ? THEME.COLORS.NEUTRAL_WHITE
                    : THEME.COLORS.PRIMARY_BLUE,
                  backgroundColor: historyOpen
                    ? THEME.COLORS.PRIMARY_BLUE
                    : 'transparent',
                  fontSize: '0.82rem',
                  fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                  '&:hover': {
                    backgroundColor: historyOpen
                      ? THEME.COLORS.PRIMARY_BLUE
                      : THEME.COLORS.PRIMARY_BLUE + '14',
                    borderColor: THEME.COLORS.PRIMARY_BLUE
                  }
                }}
              >
                History
              </Button>
              {historyChart && (
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    color: THEME.COLORS.NEUTRAL_GRAY,
                    fontStyle: 'italic'
                  }}
                >
                  Viewing:&nbsp;
                  <Box
                    component='span'
                    sx={{
                      fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                      fontStyle: 'normal',
                      color: THEME.COLORS.NEUTRAL_GRAY_DARK
                    }}
                  >
                    {historyChart}
                  </Box>
                </Typography>
              )}
            </Box>

            <ChartHistoryDrawer
              id='merin-history'
              open={historyOpen}
              onClose={() => setHistoryOpen(false)}
              chartHistory={chartHistory}
              onSelectChart={(url, label) => {
                setDisplayedSrc(url);
                setHistoryChart(label);
                setHistoryOpen(true);
              }}
            />
            <Box
              sx={{
                position: 'relative',
                minHeight: { xs: 200, sm: 260, md: 320 }
              }}
              aria-busy={isLoading || !displayedSrc}
            >
              <Box
                component='img'
                src={displayedSrc || ''}
                alt={`Merin Chart - ${
                  chartType === 'performance' ? 'Performance' : 'Exposure'
                } ${chartView === 'week' ? 'Weekly' : 'Monthly'}`}
                sx={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  borderRadius: '4px',
                  opacity: isLoading || !displayedSrc ? 0 : 1,
                  transition: 'opacity 0.2s ease'
                }}
                onLoad={() => setIsLoading(false)}
                onError={() => setIsLoading(false)}
              />
              {(isLoading || !displayedSrc) && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <CircularProgress
                    sx={{ color: THEME.COLORS.PRIMARY_BLUE }}
                    size={32}
                  />
                </Box>
              )}
            </Box>
          </Box>

          {/* Performance Statistics */}
          <Box
            sx={{
              backgroundColor: THEME.COLORS.NEUTRAL_WHITE,
              border: `1px solid ${THEME.COLORS.NEUTRAL_GRAY_LIGHT}`,
              borderRadius: '8px',
              padding: { xs: 3, sm: 4 },
              mb: ELEMENT_SPACING
            }}
          >
            <Typography
              sx={{
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                fontSize: '1.5rem',
                fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
                color: THEME.COLORS.PRIMARY_BLUE,
                mb: 3,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              Performance Statistics
            </Typography>
            <Grid container spacing={3}>
              {performanceStats.map(stat => (
                <Grid item xs={12} sm={6} md={4} key={stat.label}>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '1rem',
                        color: THEME.COLORS.NEUTRAL_GRAY_DARK,
                        mb: 0.5
                      }}
                    >
                      {stat.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '2.5rem',
                        fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
                        color: THEME.COLORS.NEUTRAL_BLACK
                      }}
                    >
                      {stat.value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

        {/* Introduction Section */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.PRIMARY_BLUE,
              mb: ELEMENT_SPACING
            }}
          >
            Introduction
          </Typography>

          <SectionContent
            initialSpacing={false}
            text={[
              {
                text: "MERIN is a systematic macro overlay strategy released on 10.11.2025 and now fully active within MarQ Capital's strategy suite.",
                fontSize: '1.3rem',
                marginBottom: ELEMENT_SPACING,
                color: THEME.COLORS.NEUTRAL_GRAY_DARK
              },
              {
                text: 'It delivers adaptable exposure across global asset classes, adding a macro-driven layer that strengthens institutional portfolios through resilience, diversification, and disciplined return potential.',
                fontSize: '1.3rem',
                marginBottom: ELEMENT_SPACING,
                color: THEME.COLORS.NEUTRAL_GRAY_DARK
              },
              {
                text: 'Built on proprietary signal sets, MERIN captures directional and regime-shift opportunities while maintaining robust, rule-based risk control.',
                fontSize: '1.3rem',
                marginBottom: 0,
                color: THEME.COLORS.NEUTRAL_GRAY_DARK
              }
            ]}
          />
        </Box>

        {/* Key Features Section */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.PRIMARY_BLUE,
              mb: ELEMENT_SPACING
            }}
          >
            Key Features
          </Typography>

          <Box sx={{ mb: SECTION_SPACING }}>
            <StyledList
              variant='numbered'
              items={[
                {
                  title: 'Macro-Driven Signal Suite',
                  text: "Developed around macroeconomic, rate and trend indicators, MERIN's algorithmic engine aims to identify turning points and dynamically adjust exposures across equities, rates, FX and commodities."
                },
                {
                  title: 'Regime-Agnostic Adaptability',
                  text: 'Free from narrative bias, the model transitions fluidly across growth, inflation and risk-off environments, applying the appropriate overlays as conditions evolve.'
                },
                {
                  title: 'Portfolio Integration & Diversification',
                  text: 'The strategy is being engineered to maintain low correlation with traditional benchmarks, allowing for smooth integration into multi-asset portfolios while preserving liquidity and transparency.'
                },
                {
                  title: 'Governance & Infrastructure',
                  text: "As part of MarQ Capital's incubation suite, MERIN operates under the same institutional governance, execution and oversight frameworks applied across all active strategies."
                }
              ]}
            />
          </Box>
        </Box>

        {/* Why MERIN Section */}
        <Box
          sx={{
            mb: SECTION_SPACING,
            backgroundColor: THEME.COLORS.PRIMARY_BLUE,
            borderRadius: '8px',
            padding: { xs: 3, sm: 4, md: 5 },
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        >
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.NEUTRAL_WHITE,
              mb: ELEMENT_SPACING
            }}
          >
            Why MERIN
          </Typography>

          <Typography
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
              fontSize: '1.3rem',
              color: THEME.COLORS.NEUTRAL_WHITE,
              fontStyle: 'italic',
              lineHeight: 1.8,
              mb: ELEMENT_SPACING
            }}
          >
            In volatile macro environments, adaptability is edge.
          </Typography>

          <Typography
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
              fontSize: '1.3rem',
              color: THEME.COLORS.NEUTRAL_WHITE,
              fontStyle: 'italic',
              lineHeight: 1.8,
              mb: ELEMENT_SPACING
            }}
          >
            MERIN is built to anticipate, not react, evolving as market regimes
            shift.
          </Typography>
        </Box>

        {/* Related Documents Section */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.PRIMARY_BLUE,
              mb: ELEMENT_SPACING
            }}
          >
            Related Documents
          </Typography>
          <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
            <Grid item xs={12} sm={6} md={4}>
              <Button
                variant='outlined'
                startIcon={<DescriptionIcon />}
                onClick={() => handleDocumentClick('full-factsheet')}
                sx={{
                  width: '100%',
                  height: '100%',
                  minHeight: '80px',
                  padding: 3,
                  borderColor: THEME.COLORS.PRIMARY_BLUE,
                  color: THEME.COLORS.PRIMARY_BLUE,
                  fontSize: '1.1rem',
                  fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                  textTransform: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  '&:hover': {
                    borderColor: THEME.COLORS.PRIMARY_BLUE,
                    backgroundColor: THEME.COLORS.PRIMARY_BLUE_LIGHT + '22'
                  }
                }}
              >
                <Box>Full Factsheet</Box>
                <Box sx={{ fontSize: '0.85rem', fontWeight: 'normal' }}>
                  Complete strategy overview
                </Box>
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Button
                variant='outlined'
                startIcon={<DescriptionIcon />}
                onClick={() => handleDocumentClick('strategy-factsheet')}
                sx={{
                  width: '100%',
                  height: '100%',
                  minHeight: '80px',
                  padding: 3,
                  borderColor: THEME.COLORS.PRIMARY_BLUE,
                  color: THEME.COLORS.PRIMARY_BLUE,
                  fontSize: '1.1rem',
                  fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                  textTransform: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  '&:hover': {
                    borderColor: THEME.COLORS.PRIMARY_BLUE,
                    backgroundColor: THEME.COLORS.PRIMARY_BLUE_LIGHT + '22'
                  }
                }}
              >
                <Box>Strategy Description</Box>
                <Box sx={{ fontSize: '0.85rem', fontWeight: 'normal' }}>
                  Key metrics & methodology
                </Box>
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Contact Section */}
        <Box sx={{ mb: SECTION_SPACING }}>
          <Typography
            variant='h2'
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
              fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.BOLD,
              fontSize: { xs: '1.8rem', sm: '2rem', md: '2.2rem' },
              color: THEME.COLORS.PRIMARY_BLUE,
              mb: ELEMENT_SPACING
            }}
          >
            Get in Touch
          </Typography>

          <SectionContent
            initialSpacing={false}
            text={[
              {
                text: 'For more information about MERIN or to discuss how this strategy may complement your portfolio, please contact our team.',
                fontSize: '1.3rem',
                marginBottom: ELEMENT_SPACING,
                color: THEME.COLORS.NEUTRAL_GRAY_DARK
              },
              {
                text: 'This information has been prepared solely for informational purposes and is not an offer to buy or sell or a solicitation of an offer to buy or sell any security or instrument or to participate in any trading strategy. Past performance is not necessarily indicative of future results.',
                fontSize: '1rem',
                marginBottom: 0,
                color: THEME.COLORS.NEUTRAL_GRAY,
                fontStyle: 'italic'
              }
            ]}
          />
        </Box>
      </Box>
    </>
  );
};

export default MerinPage;
